import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  FormEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/joy/Tooltip';
import {
  Search,
  Activity,
  Wrench,
  X,
  FileDown,
  Eye,
  Filter,
  ChevronRight,
  ChevronDown,
  RotateCw,
  ArrowDownToLine,
  FileSpreadsheet,
  UserCog,
  Info,
} from 'lucide-react';

import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid';

import { Sensor } from '../../../types';

import toast from 'react-hot-toast';

import { CertificateFormData } from '../types/certificate';
import { certificateService, type CertificateChangeReason } from '../services/certificate.service';
import { formatSensorType } from '../../../shared/utils/formatters';

import { sensorService, SensorTypeService, SearchService } from '../services';
import type { SensorType } from '../../../types';
import type { UnifiedSearchResult } from '../services';

import { useModal } from '../../../shared/components/providers/ModalContext';

import { getSensorSchemaFromCategoryAndType } from '../sensors/sensorSchemas';
import { CertificateFieldConfig } from '../sensors/types';
import { HIDDEN_CATEGORIES } from '../sensors/categoryUtils';
import { formatMeasurementRangeLabel } from '../utils/rangeUnit';
import {
  buildCertificateFormData,
  canShowCertificateAction,
} from '../utils/certificateFormData';
import {
  buildCertificateDisplayContext,
  resolveCertificateFieldUnit,
  formatAtmPressureForCertificateForm,
  convertMmHgToMeasurementUnit,
} from '../utils/certificateFormContext';
import { DailyEnvironmentalDataService } from '../../manage/services/dailyEnvironmentalData.service';

const CalibrationManagement: React.FC = () => {
  const navigate = useNavigate();

  // Modal context

  const { closeModal } = useModal();

  // Search and filter states

  const [searchTerm, setSearchTerm] = useState('');

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedMeasurementRange, setSelectedMeasurementRange] = useState('');
  const [selectedCreatedBy, setSelectedCreatedBy] = useState('');
  const [selectedCreatedDateFrom, setSelectedCreatedDateFrom] = useState('');
  const [selectedCreatedDateTo, setSelectedCreatedDateTo] = useState('');

  // Sidebar filter states (temporary states before applying)
  const [tempCategory, setTempCategory] = useState('');
  const [tempType, setTempType] = useState('');
  const [tempModel, setTempModel] = useState('');
  const [tempMeasurementRange, setTempMeasurementRange] = useState('');
  const [tempCreatedBy, setTempCreatedBy] = useState('');
  const [tempCreatedDateFrom, setTempCreatedDateFrom] = useState('');
  const [tempCreatedDateTo, setTempCreatedDateTo] = useState('');

  // Sidebar state
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  // Start New Calibration dropdown
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const startMenuRef = React.useRef<HTMLDivElement>(null);

  // Sensor types data for filters
  const [sensorTypes, setSensorTypes] = useState<SensorType[]>([]);
  const [isLoadingSensorTypes, setIsLoadingSensorTypes] = useState(false);

  // Unified search states
  const [searchResults, setSearchResults] = useState<UnifiedSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Ref for search dropdown to handle click outside
  const searchDropdownRef = React.useRef<HTMLDivElement>(null);

  // Ref for debounce timeout
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Ref to track if we're applying filters (to prevent reset logic)
  const isApplyingFiltersRef = React.useRef(false);

  // Pagination states

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0, // DataGrid uses 0-based page indexing
  });
  const [totalItems, setTotalItems] = useState(0);

  // Test Certificate state
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [certificateFormData, setCertificateFormData] =
    useState<CertificateFormData | null>(null);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const [isPreviewingCertificate, setIsPreviewingCertificate] = useState(false);
  const [isFillingTodaysData, setIsFillingTodaysData] = useState(false);
  const [certificatePreviewHtml, setCertificatePreviewHtml] = useState<
    string | null
  >(null);
  const [certificatePreviewZoom, setCertificatePreviewZoom] = useState(1);
  const [initialCertificateFormData, setInitialCertificateFormData] = useState<CertificateFormData | null>(null);
  const [certificateChangeReasons, setCertificateChangeReasons] = useState<CertificateChangeReason[]>([]);
  const [selectedCertificateChangeReason, setSelectedCertificateChangeReason] = useState("");
  const [selectedCertificateCalibration, setSelectedCertificateCalibration] =
    useState<{
      sensor: Sensor;
      calibration: any;
      sensorType?: string;
      sensorCategory?: string;
      sensorModel?: string;
    } | null>(null);

  // Dynamic sensors array - will be populated from API
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [isLoadingSensors, setIsLoadingSensors] = useState(false);


  // Track which sensors have their calibration rows expanded
  const [expandedSensorCalibrations, setExpandedSensorCalibrations] = useState<
    Set<string>
  >(new Set());

  // Function to load sensor types for filters
  const loadSensorTypes = useCallback(async () => {
    try {
      setIsLoadingSensorTypes(true);
      const types = await SensorTypeService.getAll();
      setSensorTypes(types);
    } catch (error) {
      toast.error('Failed to load sensor types for filters.');
      setSensorTypes([]);
    } finally {
      setIsLoadingSensorTypes(false);
    }
  }, []);

  // Function to load sensors from API (server-side pagination)
  const loadSensors = useCallback(
    async (paginationOverride?: GridPaginationModel) => {
      const pageToLoad = (paginationOverride?.page ?? paginationModel.page) + 1; // API uses 1-based
      const limitToUse =
        paginationOverride?.pageSize ?? paginationModel.pageSize;

      try {
        setIsLoadingSensors(true);

        // Build filter object
        const filters: any = {};
        if (selectedCategory) filters.sensor_type_category = selectedCategory;
        if (selectedType) filters.sensor_type_type = selectedType;
        if (selectedModel) filters.sensor_type_model = selectedModel;
        if (selectedMeasurementRange) {
          filters.sensor_type_measurement_range = parseFloat(
            selectedMeasurementRange,
          );
        }
        if (selectedCreatedBy) filters.created_by = selectedCreatedBy;
        // Date inputs give YYYY-MM-DD format which the API accepts
        if (selectedCreatedDateFrom) {
          filters.created_date_from = selectedCreatedDateFrom;
        }
        if (selectedCreatedDateTo) {
          filters.created_date_to = selectedCreatedDateTo;
        }

        // Load sensors with filters
        const response = await sensorService.getSensors(
          pageToLoad,
          limitToUse,
          undefined,
          filters,
        );
        const sensorsData = response?.data ?? [];

        setSensors(sensorsData);
        setTotalItems(response?.total ?? sensorsData.length);
      } catch (error) {
        toast.error('Failed to load data from API.');
        setSensors([]);
        setTotalItems(0);
      } finally {
        setIsLoadingSensors(false);
      }
    },
    [
      paginationModel,
      selectedCategory,
      selectedType,
      selectedModel,
      selectedMeasurementRange,
      selectedCreatedBy,
      selectedCreatedDateFrom,
      selectedCreatedDateTo,
    ],
  );

  // Close any open modals when component mounts (in case user navigated here from another page)
  useEffect(() => {
    closeModal();
  }, [closeModal]);

  // Load sensor types on mount
  useEffect(() => {
    loadSensorTypes();
  }, [loadSensorTypes]);

  // Fetch sensors whenever the pagination settings or filters change
  // Use a ref to debounce rapid filter changes (like when applying multiple filters)
  const loadSensorsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any pending load
    if (loadSensorsTimeoutRef.current) {
      clearTimeout(loadSensorsTimeoutRef.current);
    }

    // Debounce the API call slightly to batch multiple filter updates
    loadSensorsTimeoutRef.current = setTimeout(() => {
      loadSensors();
    }, 50); // Small delay to batch state updates

    return () => {
      if (loadSensorsTimeoutRef.current) {
        clearTimeout(loadSensorsTimeoutRef.current);
      }
    };
  }, [
    paginationModel,
    selectedCategory,
    selectedType,
    selectedModel,
    selectedMeasurementRange,
    selectedCreatedBy,
    selectedCreatedDateFrom,
    selectedCreatedDateTo,
    loadSensors,
  ]);

  // Handle click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  // Handle click outside to close the "Start New Calibration" dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startMenuRef.current && !startMenuRef.current.contains(event.target as Node)) {
        setStartMenuOpen(false);
      }
    };

    if (startMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [startMenuOpen]);

  // Debounced search effect - triggers search automatically after 0.5s delay
  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const trimmedTerm = searchTerm.trim();

    // If search term is empty, clear results
    if (!trimmedTerm) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    // Only search if at least 3 characters
    if (trimmedTerm.length < 3) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    // Set up debounce timeout
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await SearchService.unifiedSearch({
          q: trimmedTerm,
          limit: 20,
        });
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (error: any) {
        toast.error('Search failed. Please try again.');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 1000); // 1 second delay

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Extract unique values from sensor types for filters
  // Hierarchy: Type → Category → Model → Measurement Range
  const uniqueTypes = useMemo(() => {
    const types = sensorTypes
      .map((st) => st.type)
      .filter(
        (type): type is string =>
          type !== undefined && type !== null && type.trim() !== '',
      );
    return Array.from(new Set(types)).sort();
  }, [sensorTypes]);

  const uniqueCategories = useMemo(() => {
    // Filter categories by selected type (if sidebar is open, use temp values)
    let filtered = sensorTypes;
    const typeToUse = isFilterSidebarOpen ? tempType : selectedType;

    if (typeToUse) {
      filtered = filtered.filter((st) => st.type === typeToUse);
    }

    const categories = filtered
      .map((st) => st.category)
      .filter(
        (cat): cat is string =>
          cat !== undefined && cat !== null && cat.trim() !== '',
      );
    return Array.from(new Set(categories)).sort();
  }, [sensorTypes, selectedType, tempType, isFilterSidebarOpen]);

  const uniqueModels = useMemo(() => {
    // Filter by type and category (hierarchy: Type → Category → Model)
    let filtered = sensorTypes;
    const typeToUse = isFilterSidebarOpen ? tempType : selectedType;
    const categoryToUse = isFilterSidebarOpen ? tempCategory : selectedCategory;

    if (typeToUse) {
      filtered = filtered.filter((st) => st.type === typeToUse);
    }
    if (categoryToUse) {
      filtered = filtered.filter((st) => st.category === categoryToUse);
    }

    const models = filtered
      .map((st) => st.model)
      .filter(
        (model): model is string =>
          model !== undefined && model !== null && model.trim() !== '',
      );
    return Array.from(new Set(models)).sort();
  }, [
    sensorTypes,
    selectedType,
    selectedCategory,
    tempType,
    tempCategory,
    isFilterSidebarOpen,
  ]);

  const uniqueMeasurementRanges = useMemo(() => {
    // Filter by type, category, and model (hierarchy: Type → Category → Model → Measurement Range)
    let filtered = sensorTypes;
    const typeToUse = isFilterSidebarOpen ? tempType : selectedType;
    const categoryToUse = isFilterSidebarOpen ? tempCategory : selectedCategory;
    const modelToUse = isFilterSidebarOpen ? tempModel : selectedModel;

    if (typeToUse) {
      filtered = filtered.filter((st) => st.type === typeToUse);
    }
    if (categoryToUse) {
      filtered = filtered.filter((st) => st.category === categoryToUse);
    }
    if (modelToUse) {
      filtered = filtered.filter((st) => st.model === modelToUse);
    }

    const ranges = filtered
      .map((st) => st.measurement_range)
      .filter(
        (range): range is number => range !== undefined && range !== null,
      );
    const uniqueRanges = Array.from(new Set(ranges)).sort((a, b) => a - b);

    return uniqueRanges.map((range) => {
      const sensorType = filtered.find((st) => st.measurement_range === range);
      const unit = sensorType?.unit || '';
      return {
        value: range.toString(),
        label: formatMeasurementRangeLabel(range, unit, sensorType?.category),
      };
    });
  }, [
    sensorTypes,
    selectedType,
    selectedCategory,
    selectedModel,
    tempType,
    tempCategory,
    tempModel,
    isFilterSidebarOpen,
  ]);

  // Check if any filter is applied
  const hasActiveFilters = useMemo(() => {
    return !!(
      selectedCategory ||
      selectedType ||
      selectedModel ||
      selectedMeasurementRange ||
      selectedCreatedBy ||
      selectedCreatedDateFrom ||
      selectedCreatedDateTo
    );
  }, [
    selectedCategory,
    selectedType,
    selectedModel,
    selectedMeasurementRange,
    selectedCreatedBy,
    selectedCreatedDateFrom,
    selectedCreatedDateTo,
  ]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedType) count++;
    if (selectedCategory) count++;
    if (selectedModel) count++;
    if (selectedMeasurementRange) count++;
    if (selectedCreatedBy) count++;
    if (selectedCreatedDateFrom) count++;
    if (selectedCreatedDateTo) count++;
    return count;
  }, [
    selectedCategory,
    selectedType,
    selectedModel,
    selectedMeasurementRange,
    selectedCreatedBy,
    selectedCreatedDateFrom,
    selectedCreatedDateTo,
  ]);

  // Transform sensors data to expand multiple calibrations into separate rows
  // Note: calibrations are ordered latest-first from API; we want the oldest (initial) to be Calibration 1
  const expandedSensorsData = useMemo(() => {
    const expanded: Array<
      Sensor & {
        isCalibrationRow?: boolean;
        calibration?: any;
        calibrationIndex?: number;
        calibrationSequenceNumber?: number; // 1-based, oldest=1
        hasMultipleCalibrations?: boolean;
      }
    > = [];

    sensors.forEach((sensor) => {
      const calibrations = sensor.calibrations || [];
      const totalCalibrations = calibrations.length;
      const isExpanded = sensor.id
        ? expandedSensorCalibrations.has(sensor.id)
        : false;

      if (totalCalibrations === 0) {
        // No calibrations, add sensor as-is
        expanded.push(sensor);
      } else if (totalCalibrations === 1) {
        // Single calibration, add sensor with calibration data
        expanded.push({
          ...sensor,
          calibration: calibrations[0],
          calibrationIndex: 0,
          calibrationSequenceNumber: 1,
          hasMultipleCalibrations: false,
        });
      } else {
        // Multiple calibrations: API gives latest-first, so sequence number is total - index
        const rowsToRender = isExpanded ? totalCalibrations : 1; // show one if collapsed
        for (let i = 0; i < rowsToRender; i++) {
          expanded.push({
            ...sensor,
            calibration: calibrations[i],
            calibrationIndex: i,
            calibrationSequenceNumber: totalCalibrations - i, // oldest gets 1
            isCalibrationRow: i > 0, // first row shows sensor; others are continuation
            hasMultipleCalibrations: true,
          });
        }
      }
    });

    return expanded;
  }, [sensors, expandedSensorCalibrations]);


  // Reset to first page when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    selectedCategory,
    selectedType,
    selectedModel,
    selectedMeasurementRange,
    selectedCreatedBy,
    selectedCreatedDateFrom,
    selectedCreatedDateTo,
    paginationModel.pageSize,
  ]);

  // Reset dependent filters when parent filter changes (hierarchy: Type → Category → Model → Measurement Range)
  // Only reset if not applying filters (to prevent resets when applying multiple filters at once)
  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    // When Type changes, reset Category, Model, and Measurement Range
    setSelectedCategory('');
    setSelectedModel('');
    setSelectedMeasurementRange('');
  }, [selectedType]);

  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    // When Category changes, reset Model and Measurement Range
    setSelectedModel('');
    setSelectedMeasurementRange('');
  }, [selectedCategory]);

  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    // When Model changes, reset Measurement Range
    setSelectedMeasurementRange('');
  }, [selectedModel]);

  // Initialize temp filters when sidebar opens
  useEffect(() => {
    if (isFilterSidebarOpen) {
      setTempCategory(selectedCategory);
      setTempType(selectedType);
      setTempModel(selectedModel);
      setTempMeasurementRange(selectedMeasurementRange);
      setTempCreatedBy(selectedCreatedBy);
      setTempCreatedDateFrom(selectedCreatedDateFrom);
      setTempCreatedDateTo(selectedCreatedDateTo);
    }
  }, [
    isFilterSidebarOpen,
    selectedCategory,
    selectedType,
    selectedModel,
    selectedMeasurementRange,
    selectedCreatedBy,
    selectedCreatedDateFrom,
    selectedCreatedDateTo,
  ]);

  // Handle apply filters
  const handleApplyFilters = () => {
    // Set flag to prevent reset logic from firing
    isApplyingFiltersRef.current = true;

    // Apply all filters at once - React 18 will batch these updates
    // Order matters: Type first, then Category, then Model, then Measurement Range
    setSelectedType(tempType);
    setSelectedCategory(tempCategory);
    setSelectedModel(tempModel);
    setSelectedMeasurementRange(tempMeasurementRange);
    setSelectedCreatedBy(tempCreatedBy);
    setSelectedCreatedDateFrom(tempCreatedDateFrom);
    setSelectedCreatedDateTo(tempCreatedDateTo);

    // Reset flag after a short delay to ensure all state updates are processed
    setTimeout(() => {
      isApplyingFiltersRef.current = false;
    }, 100);

    setIsFilterSidebarOpen(false);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setTempCategory('');
    setTempType('');
    setTempModel('');
    setTempMeasurementRange('');
    setTempCreatedBy('');
    setTempCreatedDateFrom('');
    setTempCreatedDateTo('');
    setSelectedCategory('');
    setSelectedType('');
    setSelectedModel('');
    setSelectedMeasurementRange('');
    setSelectedCreatedBy('');
    setSelectedCreatedDateFrom('');
    setSelectedCreatedDateTo('');
    setIsFilterSidebarOpen(false);
  };

  // Unified search function (for manual trigger on Enter key)
  const handleUnifiedSearch = async () => {
    const trimmedTerm = searchTerm.trim();

    if (!trimmedTerm) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    // Check minimum length
    if (trimmedTerm.length < 3) {
      toast.error('Please enter at least 3 characters to search');
      return;
    }

    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    try {
      setIsSearching(true);
      const results = await SearchService.unifiedSearch({
        q: trimmedTerm,
        limit: 20,
      });
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error: any) {
      toast.error('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // View sensor details

  // Helper function to format date as "created before" (e.g., "2 days ago")
  const formatCreatedBefore = (dateString?: string): string => {
    if (!dateString) return 'N/A';

    // Normalize date parsing to avoid timezone shifts when backend sends ISO without timezone
    const parseDate = (value: string) => {
      const hasTimezone = /[zZ]|[+-]\d\d:?\d\d/.test(value);
      const normalized = hasTimezone ? value : `${value}Z`;
      const parsed = new Date(normalized);
      return isNaN(parsed.getTime()) ? new Date(value) : parsed;
    };

    try {
      const date = parseDate(dateString);
      const now = new Date();
      const diffInMs = Math.max(0, now.getTime() - date.getTime());
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      if (diffInYears > 0) {
        return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
      } else if (diffInMonths > 0) {
        return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
      } else if (diffInDays > 0) {
        return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
      } else if (diffInHours > 0) {
        return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffInMinutes > 0) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      return new Date(dateString).toLocaleDateString();
    }
  };

  // Status badge functions

  const getSensorStatusBadge = (status?: string) => {
    switch (status) {
      case 'New':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-blue-100 text-blue-800">New</span>
            <Info className="h-4 w-4" />
          </div>
        );

      case 'Calibrated_Pass':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-green-100 text-green-800">
              Pass
            </span>
            <Info className="h-4 w-4" />
          </div>
        );

      case 'Calibrated_Fail':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge inline-flex items-center gap-1 bg-red-100 text-red-800">
              Fail
            </span>
            <Info className="h-4 w-4" />
          </div>
        );

      case 'QA_Pass':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-green-100 text-green-800">
              QA Pass
            </span>
            <Info className="h-4 w-4" />
          </div>
        );

      case 'QA_Fail':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-red-100 text-red-800">
              QA Fail
            </span>
            <Info className="h-4 w-4" />
          </div>
        );

      case 'Dispatched':
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-yellow-100 text-yellow-800">
              Dispatched
            </span>
            <Info className="h-4 w-4" />
          </div>
        );

      default:
        return (
          <div className="flex items-center gap-1">
            <span className="status-badge bg-gray-100 text-gray-800">
              {status || 'N/A'}
              <Info className="h-4 w-4" />
            </span>
          </div>
        );
    }
  };

  // Helper to show calibration number based on type and index
  const getCalibrationLabel = (
    calibration?: any,
    calibrationIndex?: number,
    calibrationSequenceNumber?: number,
  ) => {
    if (!calibration) return 'N/A';

    const typeRaw = calibration.calibration_type || '';
    const type = typeRaw.toLowerCase();
    // Use provided sequence number (oldest=1) when available; fallback to index
    const baseNumber = calibrationSequenceNumber ?? (calibrationIndex ?? 0) + 1;

    if (type === 'initial') {
      return 'Calibration 1 (Initial)';
    }

    if (type === 'recalibration') {
      // Ensure recalibrations start from 2 even if index is 0
      const number = Math.max(2, baseNumber);
      return `Calibration ${number} (Recalibration)`;
    }

    // Fallback: use the sequence number with raw type
    const labelType = typeRaw ? ` (${typeRaw})` : '';
    return `Calibration ${baseNumber}${labelType}`;
  };

  // Helper functions for Test Certificate (similar to CalibrationResult)
  /** Normalize to YYYY-MM-DD for form state and HTML date input. */
  const toYYYYMMDD = (dateStr: string | undefined): string => {
    if (!dateStr || !dateStr.trim()) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('.');
      return `${year}-${month}-${day}`;
    }
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Always return YYYY-MM-DD for <input type="date"> so the value is never empty/invalid. */
  const dateInputValue = (stored: string | undefined): string => {
    const yyyy = toYYYYMMDD(stored);
    if (yyyy) return yyyy;
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  /** Convert YYYY-MM-DD to DD.MM.YYYY for API/certificate display. */
  const toDDMMYYYY = (dateStr: string): string => {
    if (!dateStr || !dateStr.trim()) return '';
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3 && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = parts;
      return `${day}.${month}.${year}`;
    }
    return dateStr;
  };

  const handleCertificateAction = async (sensor: Sensor, calibration: any) => {
    const result = calibration?.result;
    if (!result?.id) {
      toast.error('Calibration result not found.');
      return;
    }
    // Use certificate from sensors API response (result.certificates) - no extra API call
    const certificates = result.certificates || [];
    if (certificates.length > 0) {
      try {
        const cert = certificates[0];
        const blob = await certificateService.getDownloadBlob(cert.id);
        certificateService.triggerCertificateDownload(
          blob,
          cert.filename ?? cert.certificate_number ?? 'certificate',
        );
        toast.success('Certificate downloaded.');
      } catch {
        toast.error('Failed to download certificate.');
      }
      return;
    }
    openCertificateForm(sensor, calibration);
  };

  const openCertificateForm = (sensor: Sensor, calibration: any) => {
    try {
      const sensorType = (sensor as any).type || sensor.sensor_type?.type;
      const sensorCategory =
        (sensor as any).category || sensor.sensor_type?.category;
      const sensorModel = (sensor as any).model || sensor.sensor_type?.model;

      const calibrationData = {
        sensor,
        calibration,
        sensorType,
        sensorCategory,
        sensorModel,
      };

      setSelectedCertificateCalibration(calibrationData);

      const result = calibration?.result;
      if (!result || !sensor) {
        toast.error('Calibration result not found.');
        return;
      }

      const certSchema = getSensorSchemaFromCategoryAndType(
        sensorCategory,
        sensorType,
      );
      const certificateData = buildCertificateFormData(
        sensorCategory,
        sensorType,
        calibration,
        result,
        { sensor, calibration, schemaDefaultUnit: certSchema.unit },
      );

      if (!certificateData) {
        toast.error(
          'Test certificate is not available for this sensor type, or calibration data is incomplete.',
        );
        return;
      }

      setCertificateFormData(certificateData);
      setInitialCertificateFormData(certificateData);
      setSelectedCertificateChangeReason("");
      setCertificatePreviewHtml(null);
      setCertificatePreviewZoom(1);
      setShowCertificateForm(true);
    } catch (error) {
      toast.error('Something went wrong while preparing the certificate.');
    }
  };

  const closeCertificateForm = () => {
    setShowCertificateForm(false);
    setIsGeneratingCertificate(false);
    setIsPreviewingCertificate(false);
    setCertificatePreviewHtml(null);
    setCertificatePreviewZoom(1);
    setInitialCertificateFormData(null);
    setSelectedCertificateChangeReason("");
    setSelectedCertificateCalibration(null);
  };

  useEffect(() => {
    if (!showCertificateForm) return;

    certificateService.getChangeReasons()
      .then((reasons) => setCertificateChangeReasons(reasons.filter((reason) => reason.is_active)))
      .catch(() => {
        setCertificateChangeReasons([]);
        toast.error("Failed to load certificate change reasons.");
      });
  }, [showCertificateForm]);

  const hasCertificateFieldChanges = useMemo(() => {
    if (!certificateFormData || !initialCertificateFormData) return false;

    const fields: Array<keyof CertificateFormData> = [
      "date",
      "temperature",
      "humidity",
      "atmPressure",
      "cableLength",
      "dwtNo",
    ];

    return fields.some((field) =>
      String(certificateFormData[field] ?? "") !== String(initialCertificateFormData[field] ?? "")
    );
  }, [certificateFormData, initialCertificateFormData]);

  const getCertificatePayload = ():
    | Parameters<typeof certificateService.generate>[0]
    | null => {
    if (
      !certificateFormData ||
      !selectedCertificateCalibration?.calibration?.result?.id
    ) {
      return null;
    }

    return {
      result_id: selectedCertificateCalibration?.calibration.result.id,
      date: toDDMMYYYY(certificateFormData?.date),
      temperature: certificateFormData?.temperature,
      humidity: certificateFormData?.humidity,
      atm_pressure: certificateFormData?.atmPressure,
      cable_length: certificateFormData?.cableLength,
      dwt_no: certificateFormData?.dwtNo,
      ...(hasCertificateFieldChanges && selectedCertificateChangeReason
        ? { change_reason_id: selectedCertificateChangeReason }
        : {}),
    };
  };

  const handleCertificatePreview = async () => {
    const payload = validateCertificateForm(false);
    if (!payload) {
      toast.error('Certificate data is missing.');
      return;
    }

    try {
      setIsPreviewingCertificate(true);
      const html = await certificateService.preview(payload);
      if (!html) {
        toast.error('Certificate preview is empty.');
        return;
      }
      setCertificatePreviewHtml(html);
    } catch {
      toast.error('Failed to load certificate preview. Please try again.');
    } finally {
      setIsPreviewingCertificate(false);
    }
  };

  const handleCertificateFieldChange = (
    field: keyof CertificateFormData,
    value: string,
  ) => {
    setCertificateFormData((prev) => {
      if (!prev) return prev;
      if (field === 'date') {
        return { ...prev, date: value.trim() || prev.date };
      }
      return { ...prev, [field]: value };
    });
  };

  /** Overwrite whichever certificate fields this sensor's form shows with today's admin-entered environmental data. */
  const handleFillTodaysEnvironmentalData = async () => {
    if (!selectedCertificateCalibration) return;

    setIsFillingTodaysData(true);
    try {
      const todaysData = await DailyEnvironmentalDataService.getToday();

      const schema = getSensorSchemaFromCategoryAndType(
        selectedCertificateCalibration.sensorCategory,
        selectedCertificateCalibration.sensorType,
      );
      const visibleFieldKeys = new Set(
        (schema?.certificateFormConfig?.fields || []).map((field) => field.key),
      );
      const certificateDisplayContext = buildCertificateDisplayContext({
        sensor: selectedCertificateCalibration.sensor,
        calibration: selectedCertificateCalibration.calibration,
        schemaDefaultUnit: schema.unit,
      });

      setCertificateFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };

        if (visibleFieldKeys.has('date')) {
          next.date = dateInputValue(todaysData.entry_date);
        }
        if (visibleFieldKeys.has('temperature')) {
          next.temperature = `${Math.round(todaysData.ambient_temperature)} °C`;
        }
        if (visibleFieldKeys.has('humidity')) {
          next.humidity = `${Math.round(todaysData.humidity)} %`;
        }
        if (visibleFieldKeys.has('atmPressure')) {
          const converted = convertMmHgToMeasurementUnit(
            todaysData.atm_pressure_mmhg,
            certificateDisplayContext.measurementUnit,
          );
          next.atmPressure = formatAtmPressureForCertificateForm(
            converted,
            certificateDisplayContext.measurementUnit,
          );
        }

        return next;
      });

      toast.success("Filled with today's environmental data.");
    } catch {
      toast.error("Today's environmental data has not been entered yet.");
    } finally {
      setIsFillingTodaysData(false);
    }
  };

  const validateCertificateForm = (requireChangeReason=true) => {
    if (!certificateFormData || !selectedCertificateCalibration) {
      toast.error('Certificate data is missing.');
      return null;
    }
    const payload = getCertificatePayload();
    if (!payload) {
      toast.error('Result ID not found.');
      return null;
    }

    if (requireChangeReason && hasCertificateFieldChanges && !selectedCertificateChangeReason) {
      toast.error("Please select a change reason for the updated certificate details.");
      return null;
    }

    const schema = getSensorSchemaFromCategoryAndType(
      selectedCertificateCalibration.sensorCategory,
      selectedCertificateCalibration.sensorType,
    );
    const certificateFormFields = schema?.certificateFormConfig?.fields || [];

    const missingRequiredFields: string[] = [];
    certificateFormFields.forEach((field: CertificateFieldConfig) => {
      if (field.required) {
        const value = certificateFormData[field.key];
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          String(value).trim() === ''
        ) {
          missingRequiredFields.push(field.label);
        }
      }
    });

    if (missingRequiredFields.length > 0) {
      toast.error(
        `Please fill in required fields: ${missingRequiredFields.join(', ')}`,
      );
      return null;
    }

    return payload;
  };

  const handleCertificateGenerate = async () => {
    const payload = validateCertificateForm();
    if (!payload) return;

    try {
      setIsGeneratingCertificate(true);
      const cert = await certificateService.generate(payload);
      closeCertificateForm();
      const blob = await certificateService.getDownloadBlob(cert.id);
      certificateService.triggerCertificateDownload(
        blob,
        cert.filename ?? cert.certificate_number ?? 'certificate',
      );
      toast.success('Certificate generated and downloaded.');
      await loadSensors();
    } catch {
      toast.error('Failed to generate certificate. Please try again.');
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  const handleCertificateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await handleCertificateGenerate();
  };

  const responsivePreviewHtml =
    certificatePreviewHtml?.replace(
      /<\/head>/i,
      `<style>
      html, body { max-width: 100%; overflow-x: hidden; }
      body { margin: 0; }
      img, table, video, svg { max-width: 100%; height: auto; }
      table { table-layout: fixed; word-break: break-word; }
      * { box-sizing: border-box; }
    </style></head>`,
    ) ?? null;

  const zoomedPreviewHtml =
    responsivePreviewHtml?.replace(
      /<\/head>/i,
      `<style>body { zoom: ${certificatePreviewZoom}; }</style></head>`,
    ) ?? null;

  return (
    <div className="space-y-2 sm:space-y-3">
      {/* Page Header */}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-start gap-3">
          <Wrench className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Calibration Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage sensors and view their calibration history
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-4 lg:items-center">
          {/* Table Controls */}

          {/* Action Button */}

          <div className="relative flex-shrink-0" ref={startMenuRef}>
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              aria-expanded={startMenuOpen}
              aria-haspopup="true"
              onClick={() => setStartMenuOpen((prev) => !prev)}
            >
              <Activity className="h-4 w-4 mr-2" />
              Start New Calibration
              <ChevronDown
                className={`h-4 w-4 ml-2 transition-transform duration-200 ease-out ${startMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {startMenuOpen && (
              <div
                className="absolute right-0 top-full z-50 mt-1.5 min-w-[240px] overflow-hidden rounded-[10px] border border-slate-200 bg-white py-1"
                style={{
                  boxShadow:
                    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.07)',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setStartMenuOpen(false);
                    navigate("/calibrations/start");
                  }}
                  className="mx-1.5 my-0.5 flex w-[calc(100%-12px)] items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors duration-150 hover:bg-slate-50"
                >
                  <UserCog className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" strokeWidth={2} />
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block text-[0.8rem] font-semibold leading-snug text-slate-900">
                      Single Calibration
                    </span>
                    <span className="mt-0.5 block text-[0.72rem] font-normal leading-snug text-slate-500">
                      Calibrate one sensor at a time
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStartMenuOpen(false);
                    navigate("/calibrations/bulk-start");
                  }}
                  className="mx-1.5 my-0.5 flex w-[calc(100%-12px)] items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors duration-150 hover:bg-slate-50"
                >
                  <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" strokeWidth={2} />
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block text-[0.8rem] font-semibold leading-snug text-slate-900">
                      Bulk Calibration
                    </span>
                    <span className="mt-0.5 block text-[0.72rem] font-normal leading-snug text-slate-500">
                      Upload a CSV to calibrate multiple sensors
                    </span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter */}

      <div className="card p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Unified Search */}
          <div className="relative flex-1" ref={searchDropdownRef}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>

            <input
              type="text"
              placeholder="Search sensors, calibrations, results... (min 3 chars)"
              className="input pl-9 text-sm w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUnifiedSearch();
                }
              }}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowSearchResults(true);
                }
              }}
            />

            <button
              onClick={handleUnifiedSearch}
              disabled={isSearching || !searchTerm.trim()}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Search"
            >
              {isSearching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
              ) : (
                <Search className="h-4 w-4" />
              )}
            </button>

            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Searching...</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-1">
                    {searchResults.map(
                      (result: UnifiedSearchResult, index: number) => {
                        // Helper function to extract sensor type info
                        const getSensorTypeInfo = (sensor: Sensor) => ({
                          sensorType:
                            (sensor as any).type || sensor.sensor_type?.type,
                          sensorCategory:
                            (sensor as any).category ||
                            sensor.sensor_type?.category,
                          sensorModel:
                            (sensor as any).model || sensor.sensor_type?.model,
                        });

                        // Helper function to navigate to calibration details
                        const navigateToCalibrationDetails = (
                          calibration: any,
                          sensor: Sensor,
                          resultData?: any,
                        ) => {
                          const { sensorType, sensorCategory, sensorModel } =
                            getSensorTypeInfo(sensor);
                          navigate('/calibrations/details', {
                            state: {
                              calibration,
                              sensor,
                              sensorType,
                              sensorCategory,
                              sensorModel,
                              ...(resultData && {
                                result: resultData,
                                showResult: true,
                              }),
                            },
                          });
                          setShowSearchResults(false);
                          setSearchTerm('');
                        };

                        // Generate title and description based on result type
                        let title = '';
                        let description = '';
                        let viewHandler: () => void = () => {};

                        switch (result.type) {
                          case 'sensor':
                            title = `Sensor ${result.serial_number || result.id}`;
                            description = `${result.condition || 'Unknown'} • ${result.is_active ? 'Active' : 'Inactive'}`;
                            viewHandler = async () => {
                              try {
                                const sensor =
                                  await sensorService.getSensorById(
                                    result.id,
                                    'calibrations',
                                  );
                                if (!sensor) {
                                  toast.error('Sensor not found');
                                  return;
                                }

                                const calibrations = sensor.calibrations || [];
                                if (calibrations.length === 0) {
                                  toast.error(
                                    'No calibrations found for this sensor',
                                  );
                                  return;
                                }

                                navigateToCalibrationDetails(
                                  calibrations[0],
                                  sensor,
                                );
                              } catch (error) {
                                toast.error('Failed to fetch sensor details');
                              }
                            };
                            break;
                          case 'calibration':
                            title = `Calibration ${result.id}`;
                            description = `${result.calibration_type || 'Unknown'} • Serial: ${result.serial_number || 'N/A'}`;
                            if (result.created_by) {
                              description += ` • By: ${result.created_by}`;
                            }
                            viewHandler = async () => {
                              if (!result.serial_number) {
                                toast.error(
                                  'Serial number not available for this calibration',
                                );
                                return;
                              }

                              try {
                                const sensor =
                                  await sensorService.searchSensorBySerialNumber(
                                    result.serial_number,
                                    'calibrations',
                                  );
                                if (!sensor?.id) {
                                  toast.error(
                                    `Sensor with serial number ${result.serial_number} not found`,
                                  );
                                  return;
                                }

                                const calibration = (
                                  sensor.calibrations || []
                                ).find((cal: any) => cal.id === result.id);
                                if (!calibration) {
                                  toast.error(
                                    `Calibration ${result.id} not found for this sensor`,
                                  );
                                  return;
                                }

                                navigateToCalibrationDetails(
                                  calibration,
                                  sensor,
                                );
                              } catch (error) {
                                toast.error(
                                  'Failed to find sensor for this calibration',
                                );
                              }
                            };
                            break;
                          case 'result':
                            title = `Result ${result.id}`;
                            description = `${result.status || 'Unknown'} • Serial: ${result.serial_number || 'N/A'}`;
                            if (result.comments) {
                              description += ` • ${result.comments}`;
                            }
                            viewHandler = async () => {
                              if (!result.serial_number) {
                                toast.error(
                                  'Serial number not available for this result',
                                );
                                return;
                              }

                              try {
                                const sensor =
                                  await sensorService.searchSensorBySerialNumber(
                                    result.serial_number,
                                    'calibrations',
                                  );
                                if (!sensor?.id) {
                                  toast.error(
                                    `Sensor with serial number ${result.serial_number} not found`,
                                  );
                                  return;
                                }

                                // Find calibration with matching result ID (results always present: pass or fail)
                                const calibration = (
                                  sensor.calibrations || []
                                ).find(
                                  (cal: any) => cal.result?.id === result.id,
                                );

                                if (!calibration?.result) {
                                  toast.error(
                                    `Result ${result.id} not found for this sensor`,
                                  );
                                  return;
                                }

                                navigateToCalibrationDetails(
                                  calibration,
                                  sensor,
                                  calibration.result,
                                );
                              } catch (error) {
                                toast.error('Failed to fetch result details');
                              }
                            };
                            break;
                        }

                        return (
                          <div
                            key={result.id || index}
                            onClick={viewHandler}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span
                                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      result.type === 'sensor'
                                        ? 'bg-blue-100 text-blue-800'
                                        : result.type === 'calibration'
                                          ? 'bg-green-100 text-green-800'
                                          : result.type === 'result'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {result.type?.toUpperCase()}
                                  </span>
                                </div>
                                <div className="font-medium text-sm text-gray-900 truncate">
                                  {title}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                  {description}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Apply Filter Button - toggles inline panel below (same as FDD Country Master Add) */}
          <button
            onClick={() => setIsFilterSidebarOpen((prev) => !prev)}
            className="flex items-center justify-center gap-2 whitespace-nowrap btn-primary"
          >
            <Filter className="h-4 w-4" />
            {isFilterSidebarOpen ? 'Hide Filter' : 'Apply Filter'}
            {hasActiveFilters && (
              <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-white bg-opacity-20 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Inline Filter Panel - opens below (same as FDD Manage Country Add form) */}
      {isFilterSidebarOpen && (
        <div className="card p-4 sm:p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="h-5 w-5 text-primary-600" />
              Filters
            </h2>
            <button
              onClick={() => setIsFilterSidebarOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Close filters"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Type Filter - First in hierarchy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                className="input text-sm w-full"
                value={tempType}
                onChange={(e) => {
                  setTempType(e.target.value);
                  setTempCategory('');
                  setTempModel('');
                  setTempMeasurementRange('');
                }}
                disabled={isLoadingSensorTypes}
              >
                <option value="">All Types</option>
                {isLoadingSensorTypes ? (
                  <option value="" disabled>
                    Loading...
                  </option>
                ) : (
                  uniqueTypes.map((type) => (
                    <option key={type} value={type}>
                      {formatSensorType(type)}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                className="input text-sm w-full"
                value={tempCategory}
                onChange={(e) => {
                  setTempCategory(e.target.value);
                  setTempModel('');
                  setTempMeasurementRange('');
                }}
                disabled={isLoadingSensorTypes || !tempType}
              >
                <option value="">All Categories</option>
                {isLoadingSensorTypes ? (
                  <option value="" disabled>
                    Loading...
                  </option>
                ) : !tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : (
                  uniqueCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Model Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model
              </label>
              <select
                className="input text-sm w-full"
                value={tempModel}
                onChange={(e) => {
                  setTempModel(e.target.value);
                  setTempMeasurementRange('');
                }}
                disabled={
                  isLoadingSensorTypes ||
                  !tempType ||
                  !tempCategory ||
                  uniqueModels.length === 0
                }
              >
                <option value="">All Models</option>
                {isLoadingSensorTypes ? (
                  <option value="" disabled>
                    Loading...
                  </option>
                ) : !tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : !tempCategory ? (
                  <option value="" disabled>
                    Select Category first
                  </option>
                ) : uniqueModels.length === 0 ? (
                  <option value="" disabled>
                    No models available
                  </option>
                ) : (
                  uniqueModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Measurement Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Range
              </label>
              <select
                className="input text-sm w-full"
                value={tempMeasurementRange}
                onChange={(e) => setTempMeasurementRange(e.target.value)}
                disabled={
                  isLoadingSensorTypes ||
                  !tempType ||
                  !tempCategory ||
                  !tempModel ||
                  uniqueMeasurementRanges.length === 0
                }
              >
                <option value="">All Ranges</option>
                {isLoadingSensorTypes ? (
                  <option value="" disabled>
                    Loading...
                  </option>
                ) : !tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : !tempCategory ? (
                  <option value="" disabled>
                    Select Category first
                  </option>
                ) : !tempModel ? (
                  <option value="" disabled>
                    Select Model first
                  </option>
                ) : uniqueMeasurementRanges.length === 0 ? (
                  <option value="" disabled>
                    No ranges available
                  </option>
                ) : (
                  uniqueMeasurementRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Created By Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created By
              </label>
              <input
                type="text"
                className="input text-sm w-full"
                placeholder="Creator email/username"
                value={tempCreatedBy}
                onChange={(e) => setTempCreatedBy(e.target.value)}
              />
            </div>

            {/* Created Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date From
              </label>
              <input
                type="date"
                className="input text-sm w-full"
                value={tempCreatedDateFrom}
                onChange={(e) => setTempCreatedDateFrom(e.target.value)}
              />
            </div>

            {/* Created Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date To
              </label>
              <input
                type="date"
                className="input text-sm w-full"
                value={tempCreatedDateTo}
                onChange={(e) => setTempCreatedDateTo(e.target.value)}
                min={tempCreatedDateFrom || undefined}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <button onClick={handleApplyFilters} className="btn-primary">
              Apply
            </button>
            <button onClick={handleClearFilters} className="btn-secondary">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Sensors Table */}
      {isLoadingSensors ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sensors...</p>
          </div>
        </div>
      ) : (
        <DataGrid
          columns={
            [
              // Row number column
              {
                field: 'row_number',
                headerName: '#',
                width: 60,
                sortable: false,
                filterable: true,
                disableColumnMenu: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    isCalibrationRow?: boolean;
                  };
                  if (sensor.isCalibrationRow) {
                    return <div className="text-sm text-gray-400">—</div>;
                  }
                  // Calculate row number from pagination model
                  const rowNumber =
                    paginationModel.page * paginationModel.pageSize +
                    params.api.getRowIndexRelativeToVisibleRows(params.id) +
                    1;
                  return (
                    <div className="text-sm text-gray-500 font-medium">
                      {rowNumber}
                    </div>
                  );
                },
              },
              // Sensor column
              {
                field: 'serial_number',
                headerName: 'Sensor',
                flex: 1,
                minWidth: 150,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    hasMultipleCalibrations?: boolean;
                    isCalibrationRow?: boolean;
                  };

                  if (sensor.isCalibrationRow) {
                    return (
                      <div className="text-sm text-gray-400 italic pl-4">
                        ↳ Additional calibration
                      </div>
                    );
                  }

                  const hasMultiple =
                    sensor.hasMultipleCalibrations &&
                    (sensor.calibrations?.length ?? 0) > 1;
                  const isExpanded = sensor.id
                    ? expandedSensorCalibrations.has(sensor.id)
                    : false;

                  const toggleExpansion = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!sensor.id) return;
                    setExpandedSensorCalibrations((prev) => {
                      const next = new Set(prev);
                      if (next.has(sensor.id!)) {
                        next.delete(sensor.id!);
                      } else {
                        next.add(sensor.id!);
                      }
                      return next;
                    });
                  };

                  if (hasMultiple) {
                    return (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={toggleExpansion}
                          className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          title={
                            isExpanded
                              ? 'Close all calibrations'
                              : 'Show all calibrations'
                          }
                        >
                          {params.value}
                        </button>
                        <button
                          onClick={toggleExpansion}
                          className="text-gray-500 hover:text-gray-800 flex items-center"
                          title={
                            isExpanded
                              ? 'Close all calibrations'
                              : 'Show all calibrations'
                          }
                        >
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                          />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div className="font-medium text-gray-900">
                      {params.value}
                    </div>
                  );
                },
              },
              // Sensor Type column
              {
                field: 'sensor_type_id',
                headerName: 'Sensor Type',
                flex: 1.2,
                minWidth: 180,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    isCalibrationRow?: boolean;
                  };

                  if (sensor.isCalibrationRow) {
                    return <div className="text-sm text-gray-400">—</div>;
                  }

                  const type = (sensor as any).type || sensor.sensor_type?.type;
                  const category =
                    (sensor as any).category || sensor.sensor_type?.category;
                  const model =
                    (sensor as any).model || sensor.sensor_type?.model;

                  if (type || category || model) {
                    return (
                      <div>
                        {type && (
                          <div className="font-medium text-sm">
                            {formatSensorType(type)}
                          </div>
                        )}
                        {category && (
                          <div className="font-medium text-xs text-gray-800">
                            {category}
                          </div>
                        )}
                        {model && (
                          <div className="font-medium text-xs text-gray-600">
                            {model}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div>
                      <div className="font-medium text-sm text-gray-400">
                        N/A
                      </div>
                    </div>
                  );
                },
              },
              // Calibration column
              {
                field: 'calibration_label',
                headerName: 'Calibration',
                flex: 1,
                minWidth: 150,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    calibration?: any;
                    calibrationIndex?: number;
                    calibrationSequenceNumber?: number;
                  };
                  return (
                    <span className="text-sm text-gray-900">
                      {getCalibrationLabel(
                        sensor.calibration,
                        sensor.calibrationIndex,
                        sensor.calibrationSequenceNumber,
                      )}
                    </span>
                  );
                },
              },
              // Status column
              {
                field: 'status',
                headerName: 'Status & Remarks',
                flex: 0.9,
                minWidth: 200,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    calibration?: any;
                    isCalibrationRow?: boolean;
                  };

                  let calibrationStatus: string | undefined;

                  if (sensor.calibration) {
                    calibrationStatus = sensor.calibration.status;

                    if (calibrationStatus) {
                      const statusLower = calibrationStatus.toLowerCase();
                      if (statusLower === 'failed' || statusLower === 'fail') {
                        calibrationStatus = 'Calibrated_Fail';
                      } else if (
                        statusLower === 'passed' ||
                        statusLower === 'pass'
                      ) {
                        calibrationStatus = 'Calibrated_Pass';
                      }
                    }
                  } else {
                    const calibrations = sensor.calibrations || [];
                    if (calibrations.length > 0) {
                      const latestCalibration =
                        calibrations[calibrations.length - 1];
                      calibrationStatus = latestCalibration?.status;

                      if (calibrationStatus) {
                        const statusLower = calibrationStatus.toLowerCase();
                        if (
                          statusLower === 'failed' ||
                          statusLower === 'fail'
                        ) {
                          calibrationStatus = 'Calibrated_Fail';
                        } else if (
                          statusLower === 'passed' ||
                          statusLower === 'pass'
                        ) {
                          calibrationStatus = 'Calibrated_Pass';
                        }
                      }
                    }
                  }
                  let acceptanceRemark: string | undefined;

                  if (sensor.calibration) {
                    acceptanceRemark = sensor.calibration.acceptance_remark;
                  } else {
                    const calibrations = sensor.calibrations || [];
                    if (calibrations.length > 0) {
                      const latestCalibration =
                        calibrations[calibrations.length - 1];
                      acceptanceRemark = latestCalibration?.acceptance_remark;
                    }
                  }

                  return (
                    <div>
                      <Tooltip
                        arrow
                        title={acceptanceRemark}
                        variant="outlined"
                      >
                        {getSensorStatusBadge(calibrationStatus)}
                      </Tooltip>
                    </div>
                  );
                },
              },

              // Created Date column
              {
                field: 'created_date',
                headerName: 'Created Date',
                flex: 1.1,
                minWidth: 170,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & { calibration?: any };
                  const dateToUse =
                    sensor.calibration?.created_date || params.value;

                  if (!dateToUse) {
                    return <span className="text-sm text-gray-400">N/A</span>;
                  }

                  const createdBefore = formatCreatedBefore(dateToUse);
                  const fullDate = new Date(dateToUse).toLocaleDateString();

                  return (
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {fullDate}
                      </div>
                      <div className="text-xs text-gray-500">
                        {createdBefore}
                      </div>
                    </div>
                  );
                },
              },
              // Created By column
              {
                field: 'created_by',
                headerName: 'Created By',
                flex: 1,
                minWidth: 240,
                sortable: false,
                filterable: true,
                renderCell: (params) => {
                  const sensor = params.row as Sensor & { calibration?: any };
                  const createdBy =
                    sensor.calibration?.created_by || params.value;

                  return createdBy ? (
                    <span
                      className="text-sm text-gray-900 block w-full truncate"
                      title={createdBy}
                    >
                      {createdBy}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  );
                },
              },
              // Actions column
              {
                field: 'actions',
                headerName: 'Actions',
                flex: 1,
                minWidth: 180,
                sortable: false,
                filterable: true,
                disableColumnMenu: true,
                cellClassName: 'actions-cell',
                renderCell: (params) => {
                  const sensor = params.row as Sensor & {
                    calibration?: any;
                    sensor_type?: any;
                  };
                  const calibration = (sensor as any).calibration;

                  const sensorType =
                    (sensor as any).type || sensor.sensor_type?.type;
                  const sensorCategory =
                    (sensor as any).category || sensor.sensor_type?.category;
                  const sensorModel =
                    (sensor as any).model || sensor.sensor_type?.model;

                  return (
                    <div className="flex items-center justify-start space-x-1.5">
                      <button
                        className="p-2 rounded-md text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all duration-200 border border-blue-200"
                        title="View Calibration Details"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (calibration) {
                            navigate('/calibrations/details', {
                              state: {
                                calibration: calibration,
                                sensor: sensor,
                                sensorType: sensorType,
                                sensorCategory: sensorCategory,
                                sensorModel: sensorModel,
                              },
                            });
                          } else {
                            toast.error('Calibration data not available');
                          }
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      {canShowCertificateAction(
                        sensorCategory,
                        calibration?.status,
                        !!calibration?.result,
                      ) &&
                        (() => {
                          const hasCertificate =
                            (calibration.result.certificates?.length ?? 0) > 0;
                          const isDownload = hasCertificate;
                          return (
                            <button
                              className={
                                isDownload
                                  ? 'p-2 rounded-md text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-all duration-200 border border-emerald-200'
                                  : 'p-2 rounded-md text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 transition-all duration-200 border border-amber-200'
                              }
                              title={
                                isDownload
                                  ? 'Download certificate'
                                  : 'Generate certificate'
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCertificateAction(sensor, calibration);
                              }}
                            >
                              <FileDown className="h-4 w-4" />
                            </button>
                          );
                        })()}

                      {calibration &&
                        !HIDDEN_CATEGORIES.includes(
                          sensorCategory?.toLowerCase() ?? '',
                        ) && (
                          <button
                            className="p-2 rounded-md text-violet-600 hover:text-violet-700 bg-violet-50 hover:bg-violet-100 transition-all duration-200 border border-violet-200"
                            title="Re-Calibrate"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate('/calibrations/start', {
                                state: {
                                  calibrationType: 'recalibration',
                                  sensor,
                                  prefillInputData: calibration?.input_data,
                                },
                              });
                            }}
                          >
                            <RotateCw className="h-4 w-4" />
                          </button>
                        )}
                    </div>
                  );
                },
              },
            ] as GridColDef[]
          }
          rows={expandedSensorsData}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalItems}
          loading={isLoadingSensors}
          paginationMode="server"
          getRowId={(row) =>
            row.id
              ? `${row.id}-${(row as any).calibrationIndex || 0}`
              : `unknown-${Math.random()}`
          }
          getRowHeight={() => 'auto'}
          sx={{
            backgroundColor: 'white',
            '& .MuiDataGrid-container--top [role=row]': {
              backgroundColor: '#f3f4f6',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f3f4f6',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#374151',
              padding: '12px 16px',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f9fafb',
              },
            },
            '& .MuiDataGrid-cell': {
              padding: '16px',
              fontSize: '0.875rem',
              color: '#111827',
              // Remove cell focus outline
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#f9fafb',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell.actions-cell': {
              justifyContent: 'flex-start',
            },
          }}
        />
      )}

      {/* Certificate workspace modal */}
      {showCertificateForm &&
        certificateFormData &&
        selectedCertificateCalibration && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Generate certificate
                </h3>
                <button
                  onClick={closeCertificateForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,0.65fr)_minmax(0,1.35fr)]">
                <form
                  onSubmit={handleCertificateSubmit}
                  className="min-h-0 overflow-y-auto p-6 space-y-6"
                >
                  <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                        {(() => {
                          const schema = getSensorSchemaFromCategoryAndType(
                            selectedCertificateCalibration.sensorCategory,
                            selectedCertificateCalibration.sensorType,
                          );
                          return (
                            schema?.certificateFormConfig?.title ||
                            'Certificate Details'
                          );
                        })()}
                      </h4>
                      <button
                        type="button"
                        onClick={handleFillTodaysEnvironmentalData}
                        disabled={isFillingTodaysData}
                        className="btn-primary whitespace-nowrap text-xs px-3 py-1.5"
                        title="Fill Temperature/Humidity/Atm. Pressure/Date with today's admin-entered environmental data"
                      >
                        {isFillingTodaysData ? 'Filling...' : "Today's data"}
                      </button>
                    </div>
                    {(() => {
                      const schema = getSensorSchemaFromCategoryAndType(
                        selectedCertificateCalibration.sensorCategory,
                        selectedCertificateCalibration.sensorType,
                      );
                      const description =
                        schema?.certificateFormConfig?.description;
                      return description ? (
                        <p className="text-xs text-gray-500">{description}</p>
                      ) : null;
                    })()}
                    <div className="grid grid-cols-1 gap-4">
                      {(() => {
                        const schema = getSensorSchemaFromCategoryAndType(
                          selectedCertificateCalibration.sensorCategory,
                          selectedCertificateCalibration.sensorType,
                        );
                        const certificateDisplayContext =
                          buildCertificateDisplayContext({
                            sensor: selectedCertificateCalibration.sensor,
                            calibration:
                              selectedCertificateCalibration.calibration,
                            schemaDefaultUnit: schema.unit,
                          });
                        const certificateFormFields =
                          schema?.certificateFormConfig?.fields || [];
                        return certificateFormFields.map(
                          (field: CertificateFieldConfig) => {
                            const value =
                              certificateFormData?.[field.key] ?? '';
                            let inputValue =
                              value === undefined || value === null
                                ? ''
                                : String(value);

                            if (field.type === 'date') {
                              inputValue = dateInputValue(
                                inputValue || undefined,
                              );
                            }

                            const inputType =
                              field.type === 'number'
                                ? 'number'
                                : field.type || 'text';

                            const resolvedUnit = resolveCertificateFieldUnit(
                              field.unit,
                              certificateDisplayContext,
                            );

                      return (
                        <div
                          key={field.key as string}
                          className={field.gridColumns === 2 ? "md:col-span-2" : ""}
                        >
                          <label className="block text-sm font-medium text-gray-700">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                            {resolvedUnit && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({resolvedUnit})
                              </span>
                            )}
                          </label>
                          <input
                            type={inputType}
                            className="input mt-1"
                            value={inputValue}
                            placeholder={field.placeholder}
                            required={field.required}
                            onChange={(event) =>
                              handleCertificateFieldChange(
                                field.key,
                                event.target.value
                              )
                            }
                          />
                        </div>
                      );
                    });
                  })()}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Details change reason
                      {hasCertificateFieldChanges && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </label>
                    <select
                      className="input mt-1 w-full"
                      value={selectedCertificateChangeReason}
                      onChange={(event) => setSelectedCertificateChangeReason(event.target.value)}
                      disabled={!hasCertificateFieldChanges || certificateChangeReasons.length === 0}
                      required={hasCertificateFieldChanges}
                    >
                      <option value="">
                        {certificateChangeReasons.length === 0
                          ? "No change reasons available"
                          : hasCertificateFieldChanges
                          ? "Select a change reason"
                          : "Available when certificate details change"}
                      </option>
                      {certificateChangeReasons.map((changeReason) => (
                        <option key={changeReason.id} value={changeReason.id}>
                          {changeReason.reason}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>

              <section className="min-h-0 flex flex-col border-t border-gray-200 bg-gray-100 lg:border-l lg:border-t-0">
                <div className="flex flex-wrap items-center justify-start gap-3 border-b border-gray-200 bg-white p-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Certificate Preview</h4>
                    <p className="text-xs text-gray-500">Review the certificate before downloading.</p>
                  </div>
                   <button
                    type="button"
                    onClick={handleCertificatePreview}
                    className="btn-secondary whitespace-nowrap"
                  >
                    {isPreviewingCertificate ? "Loading preview..." : "Preview"} <span className="ps-2"><Eye /></span>
                  </button>
                  
                  {certificatePreviewHtml && (
                    <>
                     <button
                      type="button"
                      onClick={handleCertificateGenerate}
                      className="btn-primary whitespace-nowrap"
                      disabled={isGeneratingCertificate}
                    >
                      {isGeneratingCertificate ? "Downloading..." : "Download"} <span className="ps-2"><ArrowDownToLine /></span>
                    </button>
                    
                    <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 p-1">
                      <button
                        type="button"
                        onClick={() => setCertificatePreviewZoom((zoom) => Math.max(0.4, Number((zoom - 0.1).toFixed(2))))}
                        className="rounded p-1.5 text-gray-600 hover:bg-white hover:text-gray-900"
                        title="Zoom out"
                        aria-label="Zoom out"
                      >
                        −
                      </button>
                      <span className="min-w-12 text-center text-xs font-medium text-gray-600">
                        {Math.round(certificatePreviewZoom * 100)}%
                      </span>
                      <button
                        type="button"
                        onClick={() => setCertificatePreviewZoom((zoom) => Math.min(2, Number((zoom + 0.1).toFixed(2))))}
                        className="rounded p-1.5 text-gray-600 hover:bg-white hover:text-gray-900"
                        title="Zoom in"
                        aria-label="Zoom in"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => setCertificatePreviewZoom(1)}
                        className="rounded px-2 py-1.5 text-xs text-gray-600 hover:bg-white hover:text-gray-900"
                        title="Reset zoom"
                        aria-label="Reset zoom"
                      >
                        Reset
                      </button>
                    </div>
                    </>
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4">
                  {certificatePreviewHtml ? (
                    <iframe
                      title="Certificate Preview"
                      srcDoc={zoomedPreviewHtml ?? ""}
                      sandbox=""
                      className="block h-full min-h-[28rem] w-full min-w-0 border-0 bg-white p-5"
                    />
                  ) : (
                    <div className="flex h-full min-h-[28rem] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
                      <div>
                        <FileDown className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="mt-3 text-sm font-medium text-gray-700">Your preview will appear here</p>
                        <p className="mt-1 text-xs text-gray-500">Complete the fields and select Preview &amp; Download.</p>
                      </div>
                    </div>
                  )}
                </div>
               
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalibrationManagement;
